<template>
  <div class="availability-selector">
    <div>
      <div class="availability-selector__item" v-if="false">
        <div class="availability-selector__item-info">
          <span class="text-body-1">{{$t('Rooms')}}</span>
        </div>
        <div class="availability-quantity">
          <Button
              rounded
              aria-label="Less"
              size="small"
              :disabled="searchStore.quantity <= 1"
              @click="() => {updateQuantity(-1)}"
          >
            <i class="icon icon-minus"></i>
          </Button>
          <div class="availability-quantity__value text-body-1">
            {{searchStore.quantity}}
          </div>
          <Button
              rounded
              aria-label="More"
              size="small"
              @click="() => {updateQuantity(1)}"
          >
            <i class="icon icon-plus"></i>
          </Button>
        </div>
      </div>
      <div v-for="availability in availabilitiesList" :key="availability.code" class="availability-selector__item">
        <div class="availability-selector__item-info">
          <span class="text-body-1">{{$t(availability.label)}}</span> <span class="text-caption color-dark-grey">{{availability.age}} {{$t('yrs')}}</span>
        </div>
        <div class="availability-quantity">
          <Button
              rounded
              aria-label="Less"
              size="small"
              :disabled="availabilityModel[availability.code] === 0"
              @click="() => {updateAvailability(availability.code, -1)}"
          >
            <i class="icon icon-minus"></i>
          </Button>
          <div class="availability-quantity__value text-body-1">
            {{availabilityModel[availability.code]}}
          </div>
          <Button
              rounded
              aria-label="More"
              size="small"
              @click="() => {updateAvailability(availability.code, 1)}"
          >
            <i class="icon icon-plus"></i>
          </Button>
        </div>
      </div>
    </div>
    <div v-if="availabilityModel['children']" class="children-age-selector">
      <div class="children-age-selector__header">Children's ages <span class="required-label">(Required)</span></div>
      <div v-for="(item, index) in availabilityModel.ages" :key="index" class="children-age-selector__item">
        <div class="text-body-1 flex-1">Child {{index + 1}}</div>
        <div :class="['children-age-selector__item-value', { 'not-valid': invalidAges[index] }]">
          <Select
            v-model="availabilityModel.ages[index]"
            :options="availableChildAgesList"
            option-label="label"
            option-value="value"
            placeholder="Age"
            @change="() => clearError(index)"
          />
        </div>
      </div>
    </div>
    <div class="flex justify-end">
      <button class="btn btn-primary confirm-button" @click="done">{{$t('Done')}}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Select from 'primevue/select';
import Button from 'primevue/button'
import {useSearchStore} from '~/store/search';

const emit = defineEmits(['done'])
const searchStore = useSearchStore()

// Track which age selectors are invalid
const invalidAges = ref<Record<number, boolean>>({})

const availabilitiesList = [
  {code: 'adults', label: 'Adults', age: '18+'},
  {code: 'children', label: 'Children', age: '0-17'},
]

const availabilityModel = computed({
  get() {
    return searchStore.criteria
  },
  set(newValue) {
    searchStore.criteria = newValue
  }
})

const maxChildAge = 17
let availableChildAgesList = Array.from(Array(maxChildAge + 1))
    .map((item, index) => ({ value: index, label: String(index) }))
    .filter(item => item.value !== 0);

function checkChildrenAges() {
  const diff = availabilityModel.value['children'] - availabilityModel.value.ages.length
  if (diff > 0) {
    availabilityModel.value.ages.push(...Array.from(Array(diff)).map(() => null))
  } else if (diff < 0) {
    availabilityModel.value.ages.splice(availabilityModel.value.ages.length + diff, -diff)
  }
}
function updateAvailability(code: string, diff: number) {
  if ((availabilityModel.value[code] + diff) >= 0) {
    availabilityModel.value[code] += diff
  }
  checkChildrenAges()
}
function updateQuantity(diff: number) {
  if ((searchStore.quantity + diff) >= 0) {
    searchStore.setQuantity(searchStore.quantity += diff)
  }
}

function clearError(index: number) {
  // Clear the error state when user selects a value
  invalidAges.value[index] = false
}

function validate() {
  const childrenAges = availabilityModel.value.ages;
  const isValid = availabilityModel.value['children'] === 0 || childrenAges.every((age: any) => age !== null);

  // Update the invalidAges state
  childrenAges.forEach((age: any, index: number) => {
    if (age === null) {
      invalidAges.value[index] = true
    } else {
      invalidAges.value[index] = false
    }
  });

  return isValid;
}

defineExpose(
    { validate }
)



function done() {
  emit('done')
}

onMounted(() => {
  checkChildrenAges()
})

</script>

<style lang="scss" scoped>
.availability-selector {
  width: 316px;
  max-width: 100%;
  padding: 16px 20px;

  &.visitors-panel--property-block {
    width: 333px;
  }

  .availability-selector__item {
    display: flex;
    align-items: center;
    &:not(:last-child) {
      padding-bottom: 20px;
    }
    .availability-selector__item-info {
      flex: 1;
    }
    .availability-quantity {
      display: flex;
      align-items: center;

      button {
        //disabled
        &:disabled {
          background-color: var(--light-grey);
          border-color: var(--light-grey);
          color: var(--grey);
        }
      }

      .availability-quantity__value {
        min-width: 32px;
        text-align: center;
      }
      :global(.p-button-rounded) {
        width: 36px;
        height: 36px;
        min-width: 36px;
        background-color: var(--blue-highlight) !important;
        border-color: var(--blue-highlight) !important;
        color: var(--blue) !important;
        margin: 4px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      :global(.p-button-rounded .icon) {
        font-size: 16px;
        line-height: 1;
      }
    }
  }
  .children-age-selector {
    border-top: 1px solid var(--grey-border);
    margin-top: 24px;
    padding-top: 24px;
    
    .children-age-selector__header {
      font-size: 16px;
      font-weight: 600;
      color: var(--black);
      margin-bottom: 20px;
      
      .required-label {
        color: var(--dark-grey);
        font-weight: 400;
        font-size: 14px;
      }
    }
    
    .children-age-selector__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;

      &:not(:last-child) {
        margin-bottom: 16px;
      }

      .text-body-1 {
        color: var(--black);
        font-size: 16px;
        font-weight: 400;
      }

      &-value {
        transition: all 0.2s ease;
        
        &:focus-within {
          outline: none;
        }
      }

      :global(.p-select) {
        width: 90px;
        min-width: 90px;
        background-color: var(--white);
        border: 1px solid var(--grey);
        border-radius: 8px;
        transition: all 0.2s ease;
        
        &:hover {
          border-color: var(--dark-grey);
        }
        
        &:focus-within,
        &.p-focus {
          border-color: var(--blue);
          box-shadow: 0 0 0 3px rgba(25, 133, 249, 0.1);
        }
      }

      :global(.p-select .p-select-label) {
        padding: 8px 12px;
        font-size: 15px;
        color: var(--black);
        font-weight: 500;
      }

      :global(.p-select .p-select-dropdown) {
        width: 32px;
        color: var(--dark-grey);
      }

      :global(.p-select .p-select-dropdown svg) {
        transition: transform 0.3s ease, color 0.2s ease;
        width: 14px;
        height: 14px;
      }

      :global(.p-select:hover .p-select-dropdown svg) {
        color: var(--black);
      }

      :global(.p-select.p-focus .p-select-dropdown svg),
      :global(.p-select.p-overlay-open .p-select-dropdown svg) {
        transform: rotate(180deg);
        color: var(--blue);
      }

      // Dropdown overlay styling
      :global(.p-select-overlay) {
        background-color: var(--white) !important;
        border: 1px solid var(--grey-border);
        border-radius: 8px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        margin-top: 4px;
      }

      :global(.p-select-overlay .p-select-list) {
        background-color: var(--white);
        padding: 4px;
      }

      :global(.p-select-overlay .p-select-list .p-select-option) {
        background-color: var(--white);
        padding: 8px 12px;
        margin: 2px 0;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s ease;
        font-size: 15px;
        color: var(--black);
        font-weight: 400;
        
        &:first-child {
          margin-top: 0;
        }
        
        &:last-child {
          margin-bottom: 0;
        }
      }

      :global(.p-select-overlay .p-select-list .p-select-option.p-focus),
      :global(.p-select-overlay .p-select-list .p-select-option:hover) {
        background-color: var(--blue-highlight) !important;
        color: var(--blue);
      }

      :global(.p-select-overlay .p-select-list .p-select-option.p-selected) {
        background-color: var(--blue-highlight);
        color: var(--blue);
        font-weight: 500;
      }
    }
  }
}
.confirm-button {
  height: 44px;
  margin-top: 24px;
  font-size: 16px;
  padding: 10px 24px;
}

.not-valid {
  :global(.p-select) {
    border-color: var(--red) !important;
    
    &:focus-within,
    &.p-focus {
      border-color: var(--red) !important;
      box-shadow: 0 0 0 3px rgba(226, 14, 14, 0.1) !important;
    }
  }
}
</style>
